import * as React from "react";

import type { CatalogItemImageInput } from "@/lib/catalog-item-image";
import { itemImageSources } from "@/lib/catalog-item-image";
import { MuiTypography } from "./mui-typography";
import { cn } from "@/lib/utils";

import { ITEM_PLACEHOLDER_ILLUSTRATION } from "./item-image-box-sizes";
import { ItemPicture } from "./item-picture";
import { MuiDeleteForeverOutlinedIcon, MuiErrorOutlineOutlinedIcon } from "./mui-spec-icons";
import { LEN_TOOLTIP_DELAY_MS, MuiTooltip } from "./mui-tooltip";

// A spec row's thumbnail — the donor's `HeroImageBox`
// (`src/components/specSheets/HeroImageBox.tsx`), which the details page's rows
// (desktop and mobile) and the Editor's draw (#695).
//
// **Shared, because the donor's is** (the spec sheets' shared folder), and
// distinct from the catalog's `ItemImageBox`: a different box — a fixed 72px
// square, contained, with its own marks — around the same picture.
//
// The box, value for value (`:68-82`): 72×72, `1px solid`, 4px radius,
// relative, a centred flex row, clipped, `isolation: isolate`. Its border is
// `errorIcon` (#9f1515) on a deleted selection or a missing image, `grey.300`
// otherwise.
//
// What it holds, in order:
//
//   - **the picture** — contained, 100% of the box, at 0.5 opacity when the
//     selection is deleted; a per-plan line's illustration in its place. The
//     donor asks its resize CDN for `?w=256`; the ladder and `srcset` reach the
//     same rendition (ADR-0003's approved divergence, the catalog's own path);
//   - **Missing Image** — an unapproved item with no image: 8px `body2` in
//     `text.secondary`, `pt 9.6`, on white;
//   - **the house** — no image and nothing pending, or no item at all, at a
//     40px max width;
//   - **one mark**, deleted winning: the deleted selection's `DeleteForever`
//     centred, or the missing image's `ErrorOutline` at 4px/4px — 16px in
//     `error.main` (#ff1744, not the border's red), each with its tooltip —
//     then Atlas's **Retired marker** (#680 Q6): the deleted glyph's shape and
//     place over an undimmed picture and the normal border, saying the item has
//     left the catalog. A deleted selection whose item is also retired shows
//     the deleted mark alone: one glyph, one sentence;
//   - **Core**, top-left: `caption` at 0.55rem/1.2, white on primary, `px 4`,
//     `py 1`, radius `0 0 4px 0`, above the marks and inert to the pointer;
//   - **the EI mark**, bottom-left at 2px, 24px high.
//
// The gates for Core and EI are the caller's, as they are the donor's
// (`showCoreBadge`, `showEIBadge`): each surface draws them by its own rule.
//
// **The marks' tooltips take the keyboard** — a tab stop that opens the box on
// focus, the #690 D6 family, a named a11y-only break with no pixel moved. The
// donor's `Icon` is a bare `div` a keyboard never reaches.

const PER_PLAN_ILLUSTRATION = "/per-plan-illustration.svg";

const DELETED_TITLE = "This item has been deleted.";
const RETIRED_TITLE = "This item has been removed from the catalog.";
const MISSING_IMAGE_TITLE =
  "There is an issue locating the item image. Add an image or report issue to the DigitalOps Team.";

interface HeroImageBoxProps {
  /** The item's hero, or nothing. */
  image: CatalogItemImageInput | null | undefined;
  /** A per-plan line: the per-plan illustration stands in for the picture. */
  perPlan?: boolean;
  /**
   * The Editor's per-plan illustration, 48px wide at its own height — the
   * `imgStyles` its row hands the box (`LineSelectionImage.tsx:43-50`, #809).
   * The details rows hand none, and theirs fills the box.
   */
  narrowPerPlan?: boolean;
  /** The item's name, as the donor's `alt`. */
  alt: string | undefined;
  onClick?: () => void;
  isPendingApproval?: boolean;
  /** The selection is soft-deleted — only reachable with the changes switch on. */
  isDeleted?: boolean;
  /** The selection's item has left the catalog — the Retired marker. */
  isRetired?: boolean;
  showCoreBadge?: boolean;
  showEiBadge?: boolean;
}

export function HeroImageBox({
  image,
  perPlan = false,
  narrowPerPlan = false,
  alt,
  onClick,
  isPendingApproval = false,
  isDeleted = false,
  isRetired = false,
  showCoreBadge = false,
  showEiBadge = false,
}: HeroImageBoxProps) {
  // The donor's `heroImage` is a URL, and a per-plan line hands it the
  // illustration's — so a per-plan line is never "missing" an image.
  const hasPicture = perPlan || Boolean(image);
  const missingImage = isPendingApproval && !hasPicture;

  return (
    <div
      data-slot="hero-image-box"
      onClick={onClick}
      className={cn(
        "relative isolate flex size-[72px] content-center justify-center overflow-hidden rounded-[4px] border border-solid",
        isDeleted || missingImage ? "border-spec-error-icon" : "border-lennar-grey-300",
      )}
    >
      {perPlan ? (
        <img
          src={PER_PLAN_ILLUSTRATION}
          alt={alt}
          className={cn(
            narrowPerPlan ? "h-auto w-12" : "size-full",
            "object-contain",
            isDeleted && "opacity-50",
          )}
        />
      ) : image ? (
        <ItemPicture
          sources={itemImageSources(image)}
          alt={alt ?? ""}
          sizes="72px"
          pictureClassName="contents"
          className={cn("size-full object-contain", isDeleted && "opacity-50")}
        />
      ) : missingImage ? (
        <div className="flex size-full flex-col items-center justify-center rounded-[4px] bg-white">
          <MuiTypography
            variant="body2"
            color="secondary"
            // The size first, then body2's line height again, which it drops.
            className="pt-[9.6px] text-[8px] leading-[1.43]"
          >
            Missing Image
          </MuiTypography>
        </div>
      ) : (
        <div className="flex justify-center">
          <img src={ITEM_PLACEHOLDER_ILLUSTRATION} alt={alt} className="size-full max-w-[2.5rem]" />
        </div>
      )}

      {/* One status icon, as `renderStatusIcon` draws (`HeroImageBox.tsx:18-
          25`): the deleted glyph wins, and the Retired marker is that glyph's
          shape (#680 Q6), so it takes the same precedence over Missing Image. */}
      {isDeleted ? (
        <Mark title={DELETED_TITLE} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <MuiDeleteForeverOutlinedIcon />
        </Mark>
      ) : isRetired ? (
        <Mark title={RETIRED_TITLE} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <MuiDeleteForeverOutlinedIcon />
        </Mark>
      ) : missingImage ? (
        <Mark title={MISSING_IMAGE_TITLE} className="top-1 left-1">
          <MuiErrorOutlineOutlinedIcon />
        </Mark>
      ) : null}

      {showCoreBadge ? (
        <span className="bg-lennar-blue-600 pointer-events-none absolute top-0 left-0 z-2 rounded-[0_0_4px_0] px-1 py-px text-[0.55rem] leading-[1.2] text-white">
          Core
        </span>
      ) : null}
      {showEiBadge ? (
        <img
          src="/ei-tiers.png"
          alt="EI Packages"
          className="pointer-events-none absolute bottom-[2px] left-[2px] z-2 h-6 w-auto object-contain"
        />
      ) : null}
    </div>
  );
}

/** One 16px `error.main` mark, its tooltip reachable by pointer and keyboard. */
function Mark({
  title,
  className,
  children,
}: {
  title: string;
  className: string;
  children: React.ReactNode;
}) {
  return (
    <MuiTooltip title={title} delayMs={LEN_TOOLTIP_DELAY_MS} asChild>
      <span
        tabIndex={0}
        aria-label={title}
        className={cn(
          "text-lennar-error focus-visible:outline-lennar-blue-600 absolute z-1 flex outline-none focus-visible:outline-2 focus-visible:outline-solid",
          className,
        )}
      >
        {children}
      </span>
    </MuiTooltip>
  );
}
